import { computed, ref } from 'vue'

import { menuService } from '@/api/services/menu'
import type { ApiCategory, ApiDetailedMenuResponse, ApiProduct } from '@/api/types/menu'
import { useRequest } from '@/composables/useRequest'
import { useLocationStore } from '@/stores/location'

/**
 * Загрузка меню из трёх источников. Перенос `useMenu`: разбор ответа,
 * отсев подкатегорий и вырезание модификаторов у обычных товаров.
 */
export function useMenu() {
  const locationStore = useLocationStore()

  const folders = ref<ApiCategory[]>([])
  const products = ref<ApiProduct[]>([])
  const deliveryPrice = ref(0)
  /** Ответ персонального меню — из него берутся ресторан и время доставки */
  const customMenu = ref<ApiDetailedMenuResponse | null>(null)

  const regionRequest = useRequest(menuService.region)
  const restaurantRequest = useRequest(menuService.restaurant)
  const customRequest = useRequest(menuService.custom)

  const loading = computed(
    () =>
      regionRequest.isLoading.value ||
      restaurantRequest.isLoading.value ||
      customRequest.isLoading.value,
  )

  const menuError = computed(
    () => regionRequest.error.value ?? restaurantRequest.error.value ?? customRequest.error.value,
  )

  /** Только корневые категории — подкатегории на верхнем уровне не показываем. */
  function apply(categories: ApiCategory[], list: ApiProduct[], delivery: number): void {
    folders.value = categories.filter((folder) => !folder.parentId)
    products.value = list
    deliveryPrice.value = delivery
  }

  async function getRegionMenu(): Promise<void> {
    customMenu.value = null
    const result = await regionRequest.execute(locationStore.regionId)
    if (result) apply(result.categories, result.products, 0)
  }

  async function getRestaurantMenu(): Promise<void> {
    customMenu.value = null
    const result = await restaurantRequest.execute(locationStore.restaurantId)
    if (result) apply(result.categories, result.products, 0)
  }

  async function getCustomMenu(): Promise<void> {
    /*
     * Регион шлём, только когда точки нет. С координатами он не помогает,
     * а мешает: бэкенд ищет зону доставки внутри указанного региона, и
     * ташкентский адрес с `RegionId=5` отвечает «вне зоны доставки». Без
     * параметра филиал находится по координатам в любом регионе — проверено
     * на Ташкенте, Фергане и Самарканде, ответ совпадает до товара.
     */
    const result = await customRequest.execute({
      OrderTypeId: locationStore.activeDelivery,
      ...(locationStore.hasCoordinates
        ? {
            Longitude: String(locationStore.longLat[0]),
            Latitude: String(locationStore.longLat[1]),
          }
        : { RegionId: locationStore.regionId }),
      ...(locationStore.restaurantId ? { RestaurantId: locationStore.restaurantId } : {}),
    })
    customMenu.value = result
    if (!result) return

    apply(result.categories, result.products, result.priceOfDelivery)
    /* Регион клиент не выбирает — берём у филиала, который нашёл бэкенд */
    locationStore.syncRegionByRestaurant(result.restaurantId)
  }

  return {
    folders,
    products,
    deliveryPrice,
    customMenu,
    loading,
    menuError,
    getRegionMenu,
    getRestaurantMenu,
    getCustomMenu,
  }
}
